/**
 * useFeatureFlags Hook
 *
 * Fetches runtime feature flags from the backend and caches them for 5 minutes.
 * Used to conditionally render payment methods and other features.
 */

import { useQuery } from "@tanstack/react-query";
import { configService } from "@/services/config.service";
import { CLIENT_FEATURE_FLAG_KEYS } from "@/services/config.service";

export function useFeatureFlags() {
  return useQuery({
    queryKey: ["config", "features"],
    queryFn: configService.getFeatures,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Returns true when the domain registration feature is enabled.
 * Defaults to false while flags are still loading. Features are opt-in and
 * require an admin-created configuration.
 */
export function useDomainRegistrationEnabled(): boolean {
  const { data } = useFeatureFlags();
  return data?.flags?.[CLIENT_FEATURE_FLAG_KEYS.domains]?.enabled ?? false;
}

/**
 * Returns true when the database management nav is enabled.
 * Defaults to false while loading (opt-in policy).
 */
export function useDatabaseManagementEnabled(): boolean {
  const { data } = useFeatureFlags();
  return data?.flags?.[CLIENT_FEATURE_FLAG_KEYS.databases]?.enabled ?? false;
}

/**
 * Returns true when zone/region information should be displayed.
 * Defaults to false while loading — disabled until the platform offers
 * more than one zone. See PlatformSetting `zone_display_enabled`.
 */
export function useZoneDisplayEnabled(): boolean {
  const { data } = useFeatureFlags();
  return data?.flags?.[CLIENT_FEATURE_FLAG_KEYS.zone]?.enabled ?? false;
}

/**
 * Returns true when DNS management information should be displayed.
 * Defaults to false while loading — disabled until DNS management is
 * enabled platform-wide. See PlatformSetting `dns_management_enabled`.
 */
export function useDnsManagementEnabled(): boolean {
  const { data } = useFeatureFlags();
  return data?.flags?.[CLIENT_FEATURE_FLAG_KEYS.dns]?.enabled ?? false;
}

/** Returns true when the Object Storage sidebar menu is enabled. */
export function useObjectStorageEnabled(): boolean {
  const { data } = useFeatureFlags();
  return (
    data?.flags?.[CLIENT_FEATURE_FLAG_KEYS.objectStorage]?.enabled ?? false
  );
}

/** Returns true when the Firewall sidebar menu is enabled. */
export function useFirewallEnabled(): boolean {
  const { data } = useFeatureFlags();
  return data?.flags?.[CLIENT_FEATURE_FLAG_KEYS.firewall]?.enabled ?? false;
}

/** Returns true when the Monitoring sidebar menu is enabled. */
export function useMonitoringEnabled(): boolean {
  const { data } = useFeatureFlags();
  return data?.flags?.[CLIENT_FEATURE_FLAG_KEYS.monitoring]?.enabled ?? false;
}

/**
 * Returns true when the floating support contact widget should be shown.
 * Defaults to false while loading (opt-in policy), so the launcher fades in
 * once flags resolve rather than flashing on every page load.
 */
export function useSupportWidgetEnabled(): boolean {
  const { data } = useFeatureFlags();
  return (
    data?.flags?.[CLIENT_FEATURE_FLAG_KEYS.supportWidget]?.enabled ?? false
  );
}

/**
 * Returns true when the console offers "Delete" on a virtual machine.
 * Defaults to false while loading (opt-in policy), so the item is absent rather than
 * shown and then withdrawn. It hides the entry points (the list's row menu and the detail
 * page's menu); the Pending-deletion tab and Restore are not gated, so a machine deleted
 * before the flag went off can still be brought back.
 *
 * Not UI-level: the backend refuses to delete a VM with VM_DELETION_DISABLED while the flag
 * is off, so showing the item regardless would only lead to a refusal.
 */
export function useVmDeletionEnabled(): boolean {
  const { data } = useFeatureFlags();
  return data?.flags?.[CLIENT_FEATURE_FLAG_KEYS.vmDeletion]?.enabled ?? false;
}

/**
 * Returns true when the console offers "Suspend" and "Resume" on a virtual machine — one flag
 * for both. Defaults to false while loading (opt-in policy). It hides them in the list's row
 * menu, the detail page and the bulk bar. Start is not gated and also brings a suspended
 * machine back, so one suspended before the flag went off is never stranded.
 *
 * Not UI-level: the backend refuses both with VM_SUSPEND_DISABLED while the flag is off.
 */
export function useVmSuspendEnabled(): boolean {
  const { data } = useFeatureFlags();
  return data?.flags?.[CLIENT_FEATURE_FLAG_KEYS.vmSuspend]?.enabled ?? false;
}

/**
 * Returns true when tenants are asked to accept the sign-up commitment.
 * Defaults to false while loading (opt-in policy), so the blocking dialog never
 * flashes on a page load before the flags resolve.
 *
 * These two are not UI-level: the backend refuses machine creation for a tenant with an
 * outstanding commitment whatever the console does, so hiding the dialog would only leave
 * the customer stuck with no way to agree.
 */
export function useSignupAgreementEnabled(): boolean {
  const { data } = useFeatureFlags();
  return (
    data?.flags?.[CLIENT_FEATURE_FLAG_KEYS.agreementSignup]?.enabled ?? false
  );
}

/** Returns true when tenants are asked to accept the machine-creation commitment. */
export function useVmCreationAgreementEnabled(): boolean {
  const { data } = useFeatureFlags();
  return (
    data?.flags?.[CLIENT_FEATURE_FLAG_KEYS.agreementVmCreation]?.enabled ??
    false
  );
}

/** Returns true when the electronic invoice menu and page are enabled. */
export function useEInvoiceEnabled(): boolean {
  const { data } = useFeatureFlags();
  return data?.flags?.[CLIENT_FEATURE_FLAG_KEYS.eInvoice]?.enabled ?? false;
}

/**
 * Returns true when customers may upgrade a machine's CPU, RAM and disks themselves.
 * Defaults to false while loading (opt-in policy), so the entry never flashes in.
 *
 * Not UI-level: while off, the quote and the upgrade order answer `VM_UPGRADE_DISABLED`, so
 * hiding the entry is a courtesy rather than the gate.
 */
export function useVmUpgradeEnabled(): boolean {
  const { data } = useFeatureFlags();
  return data?.flags?.[CLIENT_FEATURE_FLAG_KEYS.vmUpgrade]?.enabled ?? false;
}

/**
 * Returns true when customers are offered bank transfer on the payment screens (top-up, the
 * machine and storage wizards, and `OrderPaymentFlow` for renewals, upgrades and orders).
 * Defaults to false while loading (opt-in policy, like every other flag).
 *
 * UI-level only: the API does not read this flag, and a transfer already open keeps its
 * instructions and receipt upload whatever it says.
 */
export function useBankTransferEnabled(): boolean {
  const { data } = useFeatureFlags();
  return data?.flags?.[CLIENT_FEATURE_FLAG_KEYS.bankTransfer]?.enabled ?? false;
}
